import { useEffect, useMemo, useRef, useState } from "react";
import { getAdminStats, verificarDni } from "../services/api";
import UsuariosSection from "../components/admin/UsuariosSection";
import { PlanesSection } from "../components/admin/PlanesSection";
import { SuscripcionesSection } from "../components/admin/SuscripcionesSection";
import { ClasesSection } from "../components/admin/ClasesSection";
import { PlantillasSection } from "../components/admin/PlantillasSection";
import { NotificacionesSection } from "../components/admin/NotificacionesSection";
import { ContabilidadSection } from "../components/admin/ContabilidadSection";
import TenantLogo from "../components/shared/TenantLogo";
import { useTenant } from "../context/TenantContext";
import { cleanDni, formatDni } from "../utils/formatDni";

const RESET_SECONDS = 10;

function VerificarModal({ onClose, primaryColor }) {
  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearInterval(timerRef.current);
  }, []);

  const reset = () => {
    clearInterval(timerRef.current);
    setResult(null);
    setDni("");
    setError("");
    setCountdown(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const startCountdown = () => {
    setCountdown(RESET_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) { clearInterval(timerRef.current); reset(); return null; }
        return s - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = dni.trim();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    try {
      const { ok, data } = await verificarDni(trimmed);
      if (ok || data.encontrado === false) {
        setResult(data);
        startCountdown();
      } else {
        setError(data.error || "Error al verificar");
      }
    } catch {
      setError("No se pudo conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const activo = result?.encontrado && result?.activo;
  const tone = !result ? null : activo ? "green" : "red";
  const toneRgb = { green: "74,222,128", red: "248,113,113" }[tone] || "148,163,184";
  const statusColor = { green: "#4ade80", red: "#f87171" }[tone] || null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.92)", backdropFilter: "blur(16px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Glow ambient */}
      <div
        className="pointer-events-none absolute rounded-full opacity-20 blur-3xl"
        style={{
          width: 400, height: 400,
          background: `radial-gradient(circle, ${primaryColor}, transparent 70%)`,
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />

      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(145deg, #0f172a 0%, #1e293b 100%)",
          border: `1.5px solid ${primaryColor}30`,
          boxShadow: `0 32px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)`,
        }}
      >
        {/* Top accent bar */}
        <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }} />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:text-white transition"
          style={{ background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.15)" }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4"
              style={{ background: `${primaryColor}18`, border: `1.5px solid ${primaryColor}30` }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
                <line x1="6" y1="15" x2="10" y2="15"/>
                <line x1="14" y1="15" x2="18" y2="15"/>
              </svg>
            </div>
            <h2 className="text-white text-3xl font-bold tracking-tight">Verificar Socio</h2>
            <p className="text-slate-400 text-base mt-1.5">Ingresá el DNI para consultar el estado</p>
          </div>

          {result ? (
            <div className="text-center">
              {/* Status circle */}
              <div
                className="inline-flex items-center justify-center w-24 h-24 rounded-full mx-auto mb-5"
                style={{
                  background: `rgba(${toneRgb},0.1)`,
                  border: `2px solid rgba(${toneRgb},0.4)`,
                  boxShadow: `0 0 40px -10px rgba(${toneRgb},0.4)`,
                }}
              >
                <span style={{ fontSize: "2.8rem", lineHeight: 1, color: statusColor }}>
                  {activo ? "✓" : "✗"}
                </span>
              </div>

              {result.encontrado ? (
                <>
                  <div className="text-white text-2xl font-bold">{result.nombre}</div>
                  <div
                    className="inline-block mt-2 px-4 py-1 rounded-full text-sm font-semibold"
                    style={{
                      color: statusColor,
                      background: `rgba(${toneRgb},0.12)`,
                      border: `1px solid rgba(${toneRgb},0.3)`,
                    }}
                  >
                    {activo ? "✓ Socio Activo" : "✗ Membresía inactiva"}
                  </div>
                  {result.plan_nombre && (
                    <div className="text-slate-400 text-xs mt-2">{result.plan_nombre}</div>
                  )}

                  {activo ? (
                    <>
                      {result.tiene_suscripcion && (
                        <div className="flex justify-center gap-2 mt-4 flex-wrap">
                          {result.dias_restantes !== null && (
                            <div
                              className="flex flex-col items-center px-4 py-2.5 rounded-2xl"
                              style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(148,163,184,0.12)" }}
                            >
                              <span className="text-xl font-bold text-white">{result.dias_restantes}</span>
                              <span className="text-xs text-slate-400">días restantes</span>
                            </div>
                          )}
                          {result.creditos_restantes != null && (
                            <div
                              className="flex flex-col items-center px-4 py-2.5 rounded-2xl"
                              style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(148,163,184,0.12)" }}
                            >
                              <span className="text-xl font-bold text-white">{result.creditos_restantes}</span>
                              <span className="text-xs text-slate-400">créditos</span>
                            </div>
                          )}
                        </div>
                      )}
                      {result.credito_descontado && (
                        <div className="mt-3 text-xs font-semibold" style={{ color: "#4ade80" }}>
                          ✓ 1 crédito descontado
                        </div>
                      )}
                      {result.ya_registrado_hoy && (
                        <div className="mt-3 text-xs text-slate-400">Ya registrado hoy</div>
                      )}
                      {result.sin_creditos && (
                        <div
                          className="mt-4 px-4 py-2.5 rounded-xl text-xs"
                          style={{ color: "#fbbf24", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)" }}
                        >
                          Sin créditos disponibles. Consultá con el dueño.
                        </div>
                      )}
                      {result.pago_pendiente && (
                        <div
                          className="mt-4 px-4 py-2.5 rounded-xl text-xs"
                          style={{ color: "#fbbf24", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)" }}
                        >
                          {result.mensaje_pago_pendiente}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-slate-400 text-sm mt-3">{result.mensaje_inactivo}</div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-white text-lg font-semibold mt-1">DNI no encontrado</div>
                  <div className="text-slate-400 text-sm mt-1">No hay ningún socio registrado con ese DNI.</div>
                </>
              )}

              {/* Countdown bar */}
              <div className="mt-7">
                <div className="bg-slate-800/60 rounded-full h-1 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}aa)`,
                      width: `${(countdown / RESET_SECONDS) * 100}%`,
                      transition: "width 1s linear",
                    }}
                  />
                </div>
                <p className="text-slate-500 text-xs mt-2">Nueva consulta en {countdown}s</p>
              </div>

              <button
                onClick={reset}
                className="mt-4 w-full py-3 rounded-2xl text-sm font-semibold text-white transition"
                style={{
                  background: `${primaryColor}22`,
                  border: `1px solid ${primaryColor}44`,
                }}
              >
                Consultar otro DNI
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  placeholder="12.345.678"
                  value={formatDni(dni)}
                  onChange={(e) => setDni(cleanDni(e.target.value))}
                  maxLength={13}
                  autoComplete="off"
                  className="w-full rounded-2xl text-white placeholder-slate-600 text-center text-5xl font-bold tracking-[0.25em] py-7 px-4 outline-none transition"
                  style={{
                    background: "rgba(15,23,42,0.7)",
                    border: `2px solid ${dni.trim() ? `${primaryColor}60` : "rgba(71,85,105,0.5)"}`,
                    boxShadow: dni.trim() ? `0 0 0 3px ${primaryColor}15` : "none",
                    caretColor: primaryColor,
                  }}
                />
                {dni.trim().length > 0 && (
                  <div
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full cursor-pointer text-slate-400 hover:text-white transition"
                    onClick={() => setDni("")}
                    style={{ background: "rgba(71,85,105,0.4)" }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
              </div>
              {error && (
                <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-xl" style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)" }}>
                  <span className="text-red-400 text-xs">⚠</span>
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !dni.trim()}
                className="w-full mt-5 py-5 rounded-2xl text-white text-lg font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: loading || !dni.trim()
                    ? "rgba(71,85,105,0.35)"
                    : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                  boxShadow: !loading && dni.trim() ? `0 8px 24px -8px ${primaryColor}60` : "none",
                }}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                    Verificando…
                  </span>
                ) : "Consultar"}
              </button>
              <p className="text-center text-xs text-slate-600 mt-3">Solo números · sin puntos ni guiones</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const isHex = (c) => typeof c === "string" && /^#([0-9A-F]{3}){1,2}$/i.test(c);
const safeColor = (c, fallback) => (isHex(c) ? c : fallback);

export default function AdminPanel({ onLogout }) {
  const [seccionActiva, setSeccionActiva] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [showVerificar, setShowVerificar] = useState(false);

  const tenantConfig = useTenant();

  const { primaryColor, secondaryColor } = useMemo(() => {
    const primary = safeColor(tenantConfig?.color_primario, "#6366F1");
    const secondary = safeColor(tenantConfig?.color_secundario, "#4F46E5");
    return { primaryColor: primary, secondaryColor: secondary };
  }, [tenantConfig]);

  useEffect(() => {
    let mounted = true;
    async function cargarStats() {
      const data = await getAdminStats();
      if (mounted) setStats(data);
    }
    cargarStats();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("userId");
    localStorage.removeItem("isStaff");
    onLogout();
  };

  const tabs = [
    { id: "dashboard", nombre: "Dashboard" },
    { id: "usuarios", nombre: "Usuarios" },
    { id: "planes", nombre: "Planes" },
    { id: "suscripciones", nombre: "Suscripciones" },
    { id: "clases", nombre: "Clases" },
    { id: "plantillas", nombre: "Plantillas" },
    { id: "contabilidad", nombre: "Contabilidad" },
  ]

    ;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header
        className="sticky top-0 z-50 border-b bg-slate-950/50 backdrop-blur-xl"
        style={{ borderColor: `${primaryColor}22` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className="grid place-items-center rounded-2xl border"
                style={{
                  width: 52,
                  height: 52,
                  borderColor: `${primaryColor}35`,
                  backgroundColor: `${primaryColor}12`,
                }}
              >

                <TenantLogo
                  tenantConfig={tenantConfig}
                  size="medium"
                />
              </div>

              <div className="min-w-0">
                <h1 className="truncate text-xl sm:text-2xl font-semibold tracking-tight text-white">
                  {tenantConfig?.nombre || "Panel de Administración"}
                </h1>
                <p className="text-sm text-slate-400">
                  Gestión del gimnasio <span className="text-slate-600">•</span>{" "}
                  <span style={{ color: primaryColor }} className="font-medium">
                    Admin
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowVerificar(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition focus:outline-none"
                style={{
                  borderColor: `${primaryColor}44`,
                  background: `${primaryColor}15`,
                  color: primaryColor,
                }}
                title="Verificar DNI"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <line x1="2" y1="10" x2="22" y2="10"/>
                  <line x1="6" y1="15" x2="10" y2="15"/>
                  <line x1="14" y1="15" x2="18" y2="15"/>
                </svg>
                <span className="hidden sm:inline">Verificar DNI</span>
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-500/30 bg-red-600/10 px-3 py-2 text-sm font-medium text-red-300 transition hover:bg-red-600/20 hover:text-red-200 focus:outline-none"
                title="Cerrar sesión"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                <span className="hidden sm:inline">Cerrar sesión</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="pb-4">
            <div className="flex gap-2 overflow-x-auto">
              {tabs.map((t) => {
                const active = seccionActiva === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSeccionActiva(t.id)}
                    className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-medium transition
                      ${active ? "text-white" : "text-slate-300 hover:text-white bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700"}`}
                    style={
                      active
                        ? {
                          background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                          boxShadow: `0 18px 40px -28px ${primaryColor}80`,
                        }
                        : {}
                    }
                  >
                    {t.nombre}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {showVerificar && (
        <VerificarModal primaryColor={primaryColor} onClose={() => setShowVerificar(false)} />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Dashboard */}
        {seccionActiva === "dashboard" && (
          <GlassCard
            accentColor={primaryColor}
            title="Resumen"
            subtitle="Métricas principales del gimnasio"
            rightSlot={
              <span className="text-xs text-slate-400">
                {new Date().toLocaleDateString("es-AR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </span>
            }
          >
            {stats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Usuarios activos"
                  value={stats.usuarios?.activos || 0}
                  meta={stats.usuarios?.total ? `de ${stats.usuarios.total} totales` : null}
                  accentColor={primaryColor}
                />
                <StatCard
                  title="Suscripciones activas"
                  value={stats.suscripciones_activas || 0}
                  meta="al día"
                  accentColor={primaryColor}
                />
                <StatCard
                  title="Clases futuras"
                  value={stats.clases?.futuras || 0}
                  meta="programadas"
                  accentColor={primaryColor}
                />
                <StatCard
                  title="Clases hoy"
                  value={stats.clases?.hoy || 0}
                  meta="en agenda"
                  accentColor={primaryColor}
                />
              </div>
            ) : (
              <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-6 text-center">
                <p className="text-slate-300 font-medium">Cargando estadísticas...</p>
                <p className="text-sm text-slate-500 mt-1">Un momento</p>
              </div>
            )}
          </GlassCard>
        )}

        {/* Secciones */}
        {seccionActiva === "usuarios" && <UsuariosSection primaryColor={primaryColor} />}
        {seccionActiva === "planes" && <PlanesSection primaryColor={primaryColor} />}
        {seccionActiva === "suscripciones" && (
          <SuscripcionesSection primaryColor={primaryColor} />
        )}
        {seccionActiva === "clases" && <ClasesSection primaryColor={primaryColor} />}
        {seccionActiva === "plantillas" && (
          <PlantillasSection primaryColor={primaryColor} />
        )}
        {seccionActiva === "notificaciones" && (
          <NotificacionesSection primaryColor={primaryColor} />
        )}
        {seccionActiva === "contabilidad" && (
          <ContabilidadSection primaryColor={primaryColor} />
        )}
      </main>
    </div>
  );
}

/* ---------- UI helpers (en el mismo archivo) ---------- */

function GlassCard({ accentColor, title, subtitle, rightSlot, children, className = "" }) {
  return (
    <section
      className={`relative overflow-hidden rounded-2xl border bg-slate-900/60 backdrop-blur-xl
                  shadow-[0_20px_60px_-30px_rgba(0,0,0,0.7)] ${className}`}
      style={{ borderColor: `${accentColor}22` }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accentColor}, transparent)` }}
      />
      <div className="p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-white">{title}</h2>
            {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
          </div>
          {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

function StatCard({ title, value, meta, accentColor }) {
  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-800/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium tracking-wide text-slate-400">{title}</p>
          <p className="mt-2 text-2xl font-semibold text-white">{value}</p>
          {meta && <p className="mt-1 text-xs text-slate-500">{meta}</p>}
        </div>

        <div
          className="grid place-items-center rounded-xl border"
          style={{
            width: 44,
            height: 44,
            borderColor: `${accentColor}35`,
            backgroundColor: `${accentColor}12`,
            color: accentColor,
          }}
          aria-hidden="true"
        >
          <span className="text-sm font-semibold">●</span>
        </div>
      </div>
    </div>
  );
}
